import { useEffect, useState } from "react";
import "./Supplier/supplier.css";
import ContactTable from "./ContactTable/contact-table";
import { PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, AutoComplete } from "antd";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import ContactModal from "./ContactModal/contact-modal";
import { useOpenNotification } from "../../../components/Notification/notification";
import { PrefixSelector } from "./utils";
import { requireCountryCodeAndPhoneNumber } from "../../user-information/validators";
import { supplierSearch } from "../../../services/DataTransferAPI/get-all-supplier-org";
import { internalDirectoryApi } from "../../../services/DataTransferAPI/internal-directory-api";
import { formatRequesterName } from "../../../components/Shared/utils";

interface TransferContactsProps {
  onDataChange: (data: any[]) => void;
  nextPage: string;
  contactData: any[];
  errorMessage: string | null;
  contactType: string;
}

const TransferContacts = ({
  onDataChange,
  nextPage,
  contactData,
  errorMessage,
  contactType,
}: TransferContactsProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contacts, setContacts] = useState<any[]>(contactData || []);
  const [selectedContact, setSelectedContact] = useState<any | null>(null);
  const [localErrorMessage, setLocalErrorMessage] = useState<string | null>(
    errorMessage,
  ); // Local error message state
  const [addContactsFormRef] = Form.useForm();
  const [options, setOptions] = useState<any[]>([]);
  const { openNotification, contextHolder } = useOpenNotification();
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setContacts(contactData || []);
  }, [contactData]);

  useEffect(() => {
    setLocalErrorMessage(errorMessage);
  }, [errorMessage]);

  const handleCloseModal = () => {
    if (contactType === "requester") {
      setIsEditing(false);
    }

    if (selectedContact) {
      setIsModalOpen(false);
    } else {
      setIsModalOpen(false);
      addContactsFormRef.resetFields();
    }
    setOptions([]);
  };

  const showModal = () => {
    setSelectedContact(null);
    setIsModalOpen(true);
  };

  const updateData = (newData: any[]) => {
    setContacts(newData);
    onDataChange(newData);
  };

  const handleAddContact = async () => {
    try {
      const values = await addContactsFormRef.validateFields();

      if (contactType === "requester" && values.requestername) {
        values.requestername = formatRequesterName(values.requestername);
      }

      if (
        values?.[`${contactType}CountryCode`] &&
        values?.[`${contactType}Phone`]
      ) {
        values[`${contactType}Phone`] =
          `+${values[`${contactType}CountryCode`]}${values[`${contactType}Phone`]}`;
      } else if (values?.[`${contactType}Phone`]) {
        values[`${contactType}Phone`] = `${values[`${contactType}Phone`]}`;
      } else {
        values[`${contactType}Phone`] = "";
      }
      delete values[`${contactType}CountryCode`];

      setLocalErrorMessage(null);

      const duplicate = contacts.some(
        (item) =>
          item[`${contactType}email`].trim().toLowerCase() ===
            values[`${contactType}email`].trim().toLowerCase() &&
          item.key !== selectedContact?.key,
      );

      if (duplicate) {
        addContactsFormRef.setFields([
          {
            name: `${contactType}email`,
            errors: [
              `${contactType.charAt(0).toUpperCase() + contactType.slice(1)} with the same email already exists!`,
            ],
          },
        ]);
        return;
      }
      setLocalErrorMessage(null);

      if (selectedContact) {
        updateData(
          contacts.map((contact) =>
            contact.key === selectedContact.key
              ? { ...contact, ...values }
              : contact,
          ),
        );
      } else {
        updateData([...contacts, { key: Date.now().toString(), ...values }]);
      }

      handleCloseModal(); // Close modal after adding/updating
    } catch (errorInfo) {
      return;
    }
  };

  const handleEdit = (key: string) => {
    const contactToEdit = contacts.find((contact) => contact.key === key);
    if (contactToEdit) {
      let phoneNumber;
      let countryCode;

      const phoneFieldName = `${contactType}Phone`;
      const phoneValue = contactToEdit?.[phoneFieldName];

      if (phoneValue?.startsWith("+")) {
        const parsed = parsePhoneNumberWithError(phoneValue);
        phoneNumber = parsed.nationalNumber;
        countryCode = parsed.countryCallingCode;
      } else {
        phoneNumber = phoneValue || "";
        countryCode = null;
      }

      setSelectedContact({
        ...contactToEdit,
        [phoneFieldName]: phoneNumber,
        [`${contactType}CountryCode`]: countryCode,
      });
      setIsModalOpen(true);

      if (contactType === "requester") {
        setIsEditing(true);
      }
    }
  };

  const handleDelete = (key: string) => {
    const updatedContacts = contacts.filter((contact) => contact.key !== key);
    updateData(updatedContacts);
  };

  const parseRequesterResponse = (response: any): any[] => {
    const users = response?._embedded.users;
    return users
      ?.map((user: any, ind: number) => ({
        requestername: user.displayName,
        requesteremail: user.email,
        requesterCompany: user.company,
        requesterPhone: user.phone,
        requesterTitle: user.title,
        requesterArea: user.department,
        value: user.email,
        label: user.displayName,
      }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  };

  const parseSupplierResponse = (response: any): any[] => {
    return response
      .map((supplier: any) => ({
        value: supplier.email_id,
        first_name: supplier.first_name,
        last_name: supplier.last_name,
        supplier_title: supplier.supplier_title,
        area: supplier.supplier_area,
        supplier_org: supplier.supplier_org,
        phone_number: supplier.phone_number,
        email_id: supplier.email_id,
        label: `${supplier.first_name} ${supplier.last_name} (${supplier.email_id})`,
      }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  };

  const getDetailsInputFiels = () => {
    const handleSearch = (value: string) => {
      if (!value.trim()) {
        return;
      }

      if (value.length >= 3) {
        switch (contactType) {
          case "requester":
            internalDirectoryApi(value)
              .then((response: any) => {
                const parsedResponse = parseRequesterResponse(response);
                setOptions(parsedResponse);
              })
              .catch((error: any) => {
                openNotification("error", "Error fetching requester data");
                setOptions([]);
                return;
              });
            break;
          case "supplier":
            supplierSearch(value)
              .then((response: any) => {
                const formattedOptions = parseSupplierResponse(response);
                setOptions(formattedOptions);
              })
              .catch((error: any) => {
                openNotification("error", "Error fetching supplier data");
                setOptions([]);
                return;
              });
            break;
          default:
            break;
        }
      }
    };

    const handleSelect = (value: string) => {
      const selectedValue = options.find((option) => option.value === value);

      if (!selectedValue) return;

      const isRequester = contactType === "requester";
      const phoneValue = isRequester
        ? selectedValue?.requesterPhone
        : selectedValue?.phone_number;

      let phoneNumber = "";
      let callingCountryCode: string | null = null;

      if (phoneValue?.startsWith("+")) {
        const parsed = parsePhoneNumberWithError(phoneValue);
        phoneNumber = parsed.nationalNumber;
        callingCountryCode = parsed.countryCallingCode;
      } else {
        phoneNumber = phoneValue || "";
      }

      const fieldValues = {
        [`${contactType}name`]: isRequester
          ? selectedValue.requestername
          : `${selectedValue?.first_name} ${selectedValue?.last_name}`,
        [`${contactType}Title`]: isRequester
          ? selectedValue.requesterTitle
          : selectedValue.supplier_title,
        [`${contactType}Area`]: isRequester
          ? selectedValue.requesterArea
          : selectedValue.area,
        [`${contactType}Company`]: isRequester
          ? selectedValue.requesterCompany
          : selectedValue.supplier_org,
        [`${contactType}Phone`]: phoneNumber,
        [`${contactType}CountryCode`]: callingCountryCode,
        [`${contactType}email`]: isRequester
          ? selectedValue.requesteremail
          : selectedValue.email_id,
      };

      addContactsFormRef.setFieldsValue(fieldValues);
    };

    const handleChange = (value: string) => {
      if (!value.trim()) {
        addContactsFormRef.resetFields();
        setOptions([]);
      }
    };

    return (
      <Form
        form={addContactsFormRef}
        initialValues={{ remember: true }}
        id="modal-input-form-item"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
      >
        <Form.Item
          label="Name"
          name={`${contactType}name`}
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Please input Name!",
            },
          ]}
        >
          <AutoComplete
            options={options}
            onSearch={handleSearch}
            onSelect={handleSelect}
            onChange={handleChange}
            placeholder={"Enter 3 or more characters to search"}
            disabled={contactType === "requester" && isEditing}
          />
        </Form.Item>
        <Form.Item
          label="Title"
          name={`${contactType}Title`}
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Please input Title!",
            },
          ]}
        >
          <Input disabled={contactType === "requester"} />
        </Form.Item>
        <Form.Item
          label="Functional Area"
          name={`${contactType}Area`}
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Please input Functional Area!",
            },
          ]}
        >
          <Input disabled={contactType === "requester"} />
        </Form.Item>
        <Form.Item
          label="Company"
          name={`${contactType}Company`}
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Please input Company!",
            },
          ]}
        >
          <Input disabled={contactType === "requester"} />
        </Form.Item>
        <Form.Item
          label="Phone"
          name={`${contactType}Phone`}
          dependencies={[`${contactType}CountryCode`]}
          rules={[
            {
              pattern: /^\d*$/,
              message: "Phone number must contain only numbers",
            },
            ({ getFieldValue }) =>
              requireCountryCodeAndPhoneNumber(
                getFieldValue,
                `${contactType}CountryCode`,
                `${contactType}Phone`,
              ),
          ]}
        >
          <Input
            addonBefore={
              <PrefixSelector
                name={`${contactType}CountryCode`}
                dependency={`${contactType}Phone`}
              />
            }
            placeholder="Enter phone number"
            style={{ width: "100%", borderRadius: "0px" }}
          />
        </Form.Item>
        <Form.Item
          label="Email"
          name={`${contactType}email`}
          rules={[
            {
              type: "email",
              message: "The input is not valid E-mail!",
            },
            {
              required: true,
              whitespace: true,
              message: "Please input your E-mail!",
            },
            {
              pattern: /^(?![.])(?!.*[.]{2})[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/,
              message: "The email input contains invalid characters.",
            },
          ]}
        >
          <Input disabled={contactType === "requester"} />
        </Form.Item>
      </Form>
    );
  };

  return (
    <div className="supplier">
      <div className="supplier-content">
        {nextPage === "data-transfer" ? (
          <div>
            <ContactTable
              id={`${contactType}`}
              title={`${contactType.charAt(0).toUpperCase() + contactType.slice(1)}`}
              contacts={contacts}
              onDelete={handleDelete}
              onEdit={handleEdit}
              showActions={true}
            />
            <div className="contact-add-button" id="add-button">
              <Button
                type="dashed"
                onClick={showModal}
                block
                icon={<PlusOutlined />}
                style={{ fontWeight: "bold" }}
              >
                Add
              </Button>
            </div>
            {localErrorMessage && (
              <Alert
                message={localErrorMessage}
                type="error"
                style={{ marginTop: 10 }}
              />
            )}
            <ContactModal
              isModalOpen={isModalOpen}
              onClose={handleCloseModal}
              onSubmit={handleAddContact}
              inputFields={getDetailsInputFiels()}
              selectedContact={selectedContact}
              title={
                selectedContact
                  ? `Edit ${contactType.charAt(0).toUpperCase() + contactType.slice(1)}`
                  : `Add ${contactType.charAt(0).toUpperCase() + contactType.slice(1)}`
              }
              formRef={addContactsFormRef}
            />
          </div>
        ) : (
          <div>
            <ContactTable
              id={`view-${contactType}`}
              title={`${contactType.charAt(0).toUpperCase() + contactType.slice(1)}`}
              contacts={contactData}
              showActions={false}
            />
          </div>
        )}
      </div>
      {contextHolder}
    </div>
  );
};

export default TransferContacts;
